import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { AxiosError, AxiosResponse } from "axios";

import { useApi } from "@/shared/api";
import type {
  CreateMentorProfileRequest,
  DeleteMentorProfileRequest,
  MentorProfilesResponse,
  ToggleMentorProfileRequest,
} from "./api.dto";

import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { ApiResponse } from "@/shared/types";
import { useToast } from "@/shared/hooks/useToast";
import { filterObjectKeys } from "@/shared/lib/utils";

import type { MentorProfile } from "../model";
import { MentorProfileStatus } from "../model";
import { Roles } from "@/entities/user";
import { useSessionStore } from "@/entities/session";

export const useInfiniteMentorProfiles = (
  params: {
    pageSize: number;
    statuses?: MentorProfileStatus[];
  } = { pageSize: 10 },
) => {
  const { api } = useApi({ version: "v2" });

  return useInfiniteQuery<MentorProfilesResponse, AxiosError>({
    queryKey: ["mentorProfilesInf", filterObjectKeys(params)],
    initialPageParam: 1,

    queryFn: async ({ pageParam }) => {
      const { data } = await api.get("/mentors-profiles", {
        params: {
          ...params,
          currentPage: pageParam,
        },
      });
      return data;
    },

    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = params.pageSize;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },

    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useCreateMentorProfile = () => {
  const { onSuccess, onError } = useToastNotifications();
  const { api } = useApi({ version: "v2" });
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    CreateMentorProfileRequest,
    ApiResponse<MentorProfile>
  >({
    mutationKey: ["createMentorProfile"],
    mutationFn: (data) => {
      return api.post("/mentors-profiles", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      onSuccess();
      queryClient.refetchQueries({ queryKey: ["mentorProfile"] });
    },
    onError,
  });
};

export const useDeleteMentorProfile = () => {
  const { onSuccess, onError } = useToastNotifications();
  const { api } = useApi({ version: "v2" });
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    DeleteMentorProfileRequest,
    ApiResponse<MentorProfile>
  >({
    mutationKey: ["deleteMentorProfile"],
    mutationFn: (data) => {
      return api.delete(`/mentors-profiles/${data.id}`);
    },
    onSuccess: () => {
      onSuccess();
      queryClient.refetchQueries({ queryKey: ["mentorProfilesInf"] });
    },
    onError,
  });
};

export const useEditMentorProfile = () => {
  const { onSuccess, onError } = useToastNotifications();
  const { api } = useApi({ version: "v2" });
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    FormData,
    ApiResponse<MentorProfile>
  >({
    mutationKey: ["editMentorProfile"],
    mutationFn: (formData) => {
      const id = formData.get("id");
      if (!id) throw new Error("ID профиля не передан");

      return api.patch(`/mentors-profiles/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      onSuccess();
      queryClient.refetchQueries({ queryKey: ["mentorProfile"] });
    },
    onError,
  });
};

export const useToggleMentorProfile = () => {
  const { toast } = useToast();
  const { api } = useApi({ version: "v2" });
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    ToggleMentorProfileRequest,
    ApiResponse<MentorProfile>
  >({
    mutationKey: ["toggleMentorProfile"],
    mutationFn: (data) => {
      return api.patch(`/mentors-profiles/${data.id}`, data);
    },
    onSuccess: ({ data }) => {
      toast({
        title: "Удачно!",
        description:
          (data.data as MentorProfile).status === MentorProfileStatus.ACTIVE
            ? "Вы возобновили работу"
            : "Вы приостановили работу",
      });

      ["mentorProfilesInf", "mentors", "mentorProfile", data.id].forEach(
        (key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
          });
        },
      );

      return data;
    },
  });
};

export function usePauseMentorProfileAsAdmin() {
  const { user } = useSessionStore();
  const { mutateAsync: toggleProfile } = useToggleMentorProfile();

  const handlePause = (mentorProfile: MentorProfile) => {
    const isPaused = mentorProfile.status !== MentorProfileStatus.ACTIVE;
    if (user?.role !== Roles.Head && user?.role !== Roles.MasterMentor) return;

    if (isPaused) {
      toggleProfile({
        id: mentorProfile.id,
        status: MentorProfileStatus.ACTIVE,
      });
    } else {
      const toggleStatus =
        user?.role === Roles.Head
          ? MentorProfileStatus.PAUSED_BY_HEAD
          : MentorProfileStatus.PAUSED_BY_MENTOR;

      toggleProfile({ id: mentorProfile.id, status: toggleStatus });
    }
  };

  return handlePause;
}

export const useMentorProfile = ({
  userId,
  enabled = true,
}: {
  userId: string;
  enabled?: boolean;
}) => {
  const { api } = useApi({ version: "v2" });

  return useQuery<ApiResponse<MentorProfile | null>>({
    queryKey: ["mentorProfile", userId],
    queryFn: async () => {
      const { data } = await api.get(`/mentors-profiles/mentor/${userId}`);
      return data;
    },
    enabled,
  });
};
